import React from 'react';
import {
  Ad,
  AppContent,
  baseUnit,
  ErrorBoundary,
  MEDIA_BREAKPOINTS,
  OneVCNineHLC,
  OneVC,
  Carousel,
  WideScrollableArticleList,
  SectionWithTitle,
  SecondarySectionTitle,
  ExpandableHC,
  MEDIUM_SCREEN_SIZE,
  SMALL_SCREEN_SIZE,
  SportradarFactory,
  SPORTRADAR_NAMES,
  LARGE_SCREEN_SIZE,
} from 'mm-ui-components';
import { Store } from 'redux';
import { useSelector } from 'react-redux';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Theme } from '../../components/connectedComponents/Theme';
import { HeadScripts } from '../../components/connectedComponents/HeadScripts';
import { Page } from '../Page';
import { ErrorReporter } from '../../errorReporting/errorReporter.type';
import { AppActions, AppState } from '../../store/initialStateRegistration';
import { useMeasureHydrateTime } from '../useMeasureHydrateTime';
import { NavigationLayout } from '../../components/connectedComponents/NavigationLayout';
import { MetaTags } from '../../components/connectedComponents/MetaTags';
import {
  getHreflangData,
  getMetadataDescription,
  getMetadataImage,
  getMetadataTitle,
  getSectionArticlesByKey,
  getSectionTitleByKey,
  getShowMorePaginationTextByKey,
  getShowMorePaginationURLByKey,
} from '../../store/template/homePage/homePageSantiago/homePageSantiago.selectors';
import { SECTION_NAMES, SLOT_NAMES } from '../templates.const';
import {
  getAdSlotId,
  getAdSlotOnSuccess,
  getCanonicalUrl,
  getUseFormatMinutesHoursAgo,
} from '../../store/config/config.selectors';
import {
  LoadMoreButton,
  useFetchNextPosts,
} from '../../components/connectedComponents/LoadMoreButton';
import { AdditionalStyleSheet } from '../templates.utils';
import { createSIMetaImage } from '../../store/template/template.utils';

interface HomePageProps {
  store: Store<AppState, AppActions>;
  errorProvider: ErrorReporter;
}

const getArticlesInTemplate = (state: AppState) => {
  const topSectionArticles = getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION)(
    state,
  );
  const section1Articles = getSectionArticlesByKey(SECTION_NAMES.SECTION1)(
    state,
  );
  const section2Articles = getSectionArticlesByKey(SECTION_NAMES.SECTION2)(
    state,
  );
  const section3Articles = getSectionArticlesByKey(SECTION_NAMES.SECTION3)(
    state,
  );
  const section4Articles = getSectionArticlesByKey(SECTION_NAMES.SECTION4)(
    state,
  );
  const section5Articles = getSectionArticlesByKey(SECTION_NAMES.SECTION5)(
    state,
  );

  return [
    ...topSectionArticles,
    ...section1Articles,
    ...section2Articles,
    ...section3Articles,
    ...section4Articles,
    ...section5Articles,
  ];
};

export const HomePageSantiagoComponent: React.FunctionComponent<
  HomePageProps
> = ({ store, errorProvider }) => {
  useMeasureHydrateTime();
  return (
    <Page store={store} errorProvider={errorProvider}>
      <ErrorBoundary>
        <HomePageSantiagoContent />
      </ErrorBoundary>
    </Page>
  );
};

const createStyle = () => StyleSheet.create({
    sectionsWrapper: {
      [MEDIA_BREAKPOINTS.large]: {
        gridRowGap: `${baseUnit * 2}px`,
        maxWidth: `${LARGE_SCREEN_SIZE}px`,
      },
      [MEDIA_BREAKPOINTS.medium]: {
        gridRowGap: `${baseUnit * 2}px`,
        maxWidth: `${MEDIUM_SCREEN_SIZE}px`,
        margin: '0 auto',
        padding: '0 20px',
      },
      [MEDIA_BREAKPOINTS.small]: {
        gridRowGap: `${baseUnit * 2}px`,
        maxWidth: `${SMALL_SCREEN_SIZE}px`,
        margin: '0 auto',
        paddingBottom: `${baseUnit}px`,
      },
      display: 'grid',
      gridTemplateColumns: '1fr',
      margin: `${baseUnit}px auto 0`,
      justifyItems: 'center',
    },
    sportradarWrapper: {
      boxShadow: 'none',
      paddingTop: '0',
      paddingBottom: '0',
      [MEDIA_BREAKPOINTS.medium]: {
        padding: '0',
      },
    },
    sportradar: {
      margin: '0',
    },
    carouselTitle: {
      marginBottom: '20px',
    },
    wideSection: {
      margin: `${baseUnit * 2}px 0`,
    },
    lastSection: {
      paddingBottom: `${baseUnit * 2}px`,
      [MEDIA_BREAKPOINTS.medium]: {
        paddingBottom: `${baseUnit * 2}px`,
      },
    },
  });

  const moreButtonStyle = StyleSheet.create({
    style: {
      borderRadius: '4px',
      padding: '15px 30px',
    },
  }) as AdditionalStyleSheet;

const gridSection = StyleSheet.create({
  style: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gridColumnGap: '20px',
    [MEDIA_BREAKPOINTS.medium]: {
      gridTemplateColumns: '1fr',
      gridRowGap: '20px',
    },
  },
}) as AdditionalStyleSheet;

export const HomePageSantiagoContent: React.FunctionComponent = () => {
  const topSectionArticles = useSelector(
    getSectionArticlesByKey(SECTION_NAMES.TOP_SECTION),
  );
  const topSectionTitle = useSelector(
    getSectionTitleByKey(SECTION_NAMES.TOP_SECTION),
  );
  const [section1Article] = useSelector(
    getSectionArticlesByKey(SECTION_NAMES.SECTION1),
  );
  const section2Articles = useSelector(
    getSectionArticlesByKey(SECTION_NAMES.SECTION2),
  );
  const section2Title = useSelector(
    getSectionTitleByKey(SECTION_NAMES.SECTION2),
  );
  const section3Articles = useSelector(
    getSectionArticlesByKey(SECTION_NAMES.SECTION3),
  );
  const section3Title = useSelector(
    getSectionTitleByKey(SECTION_NAMES.SECTION3),
  );
  const section4Articles = useSelector(
    getSectionArticlesByKey(SECTION_NAMES.SECTION4),
  );
  const section4Title = useSelector(
    getSectionTitleByKey(SECTION_NAMES.SECTION4),
  );
  const section5Articles = useSelector(
    getSectionArticlesByKey(SECTION_NAMES.SECTION5),
  );
  const section5Title = useSelector(
    getSectionTitleByKey(SECTION_NAMES.SECTION5),
  );
  const topAdId = useSelector(getAdSlotId(SLOT_NAMES.TOP_AD));
  const topAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.TOP_AD));
  const belowTopSectionAdId = useSelector(getAdSlotId(SLOT_NAMES.BELOW_TOP_SECTION));
  const belowTopSectionAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.BELOW_TOP_SECTION));
  const oneOnOneAdId = useSelector(getAdSlotId(SLOT_NAMES.ONE_ON_ONE));
  const oneOnOneAdOnSuccess = useSelector(getAdSlotOnSuccess(SLOT_NAMES.ONE_ON_ONE));
  const metaTitle = useSelector(getMetadataTitle);
  const metaDescription = useSelector(getMetadataDescription);
  const canonical = useSelector(getCanonicalUrl);
  let pageImage = useSelector(getMetadataImage);
  pageImage = createSIMetaImage(canonical, pageImage);
  const articles = useSelector(getArticlesInTemplate);
  const hreflangData = useSelector(getHreflangData) || [];
  const useFormatMinutesHoursAgo = useSelector(getUseFormatMinutesHoursAgo);
  const style = createStyle();

  return (
    <React.Fragment>
      <MetaTags
        articles={articles}
        title={metaTitle}
        description={metaDescription}
        pageImage={pageImage}
        alternateUrls={hreflangData}
      />
      <HeadScripts />
      <Theme>
        <AppContent>
          <NavigationLayout mainWrapperConfigThemeKey="mainContent" isFullBleed noMarginTop>
            <Ad id={topAdId} onSuccess={topAdOnSuccess} setMinHeight />
            <div className={css(style.sectionsWrapper)}>
              <SectionWithTitle>
                <OneVCNineHLC
                  cards={topSectionArticles}
                  displayName={topSectionTitle}
                  useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
                />
              </SectionWithTitle>
              <Ad id={belowTopSectionAdId} onSuccess={belowTopSectionAdOnSuccess} setMinHeight showInDesktop={false} />
              <SectionWithTitle additionalStyle={gridSection}>
                <OneVC
                  card={section1Article}
                  useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
                  imageWidths={{ small: 360, medium: 360, large: 540 }}
                />
                <SportradarFactory name={SPORTRADAR_NAMES.GOLF_LEADERBOARD} wrapperStyle={style.sportradarWrapper} widgetStyle={style.sportradar} />
              </SectionWithTitle>
              <SectionWithTitle>
                <div className={css(style.carouselTitle)}>
                  <SecondarySectionTitle>
                    {section2Title}
                  </SecondarySectionTitle>
                </div>
                <Carousel
                  itemType="VerticalCard"
                  itemsProps={section2Articles}
                  useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
                />
              </SectionWithTitle>
            </div>
            <Ad id={belowTopSectionAdId} onSuccess={belowTopSectionAdOnSuccess} setMinHeight showInTablet={false} showInMobile={false} />
            <div className={css(style.wideSection)}>
              <WideScrollableArticleList
                cardsProps={section3Articles}
                paddingFactors={{ large: 0.5, medium: 0.5, small: 0.5 }}
                useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
                cardsSectionDisplayName={section3Title}
              />
            </div>
            <div className={css(style.sectionsWrapper, style.lastSection)}>
              <SectionWithTitle>

                <div className={css(style.carouselTitle)}>
                  <SecondarySectionTitle>{section4Title}</SecondarySectionTitle>
                </div>
                <Carousel
                  itemType="VerticalCard"
                  itemsProps={section4Articles}
                  useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
                />
              </SectionWithTitle>
              <Ad id={oneOnOneAdId} onSuccess={oneOnOneAdOnSuccess} setMinHeight />
              <SectionWithTitle>
                <div className={css(style.carouselTitle)}>
                  <SecondarySectionTitle>{section5Title}</SecondarySectionTitle>
                </div>
                <ExpandableHC
                  cards={section5Articles}
                  paddingFactors={{ large: 0, medium: 0, small: 0 }}
                  useFormatMinutesHoursAgo={useFormatMinutesHoursAgo}
                />
                <LoadMoreFeedPostsButtonConnected />
              </SectionWithTitle>
            </div>
          </NavigationLayout>
        </AppContent>
      </Theme>
    </React.Fragment>
  );
};

export const LoadMoreFeedPostsButtonConnected = () => {
  const sectionKey = SECTION_NAMES.SECTION5;
  const shouldDisplay = !!useSelector(getShowMorePaginationURLByKey(sectionKey));
  const text = useSelector(getShowMorePaginationTextByKey(sectionKey));
  const onClick = useFetchNextPosts(sectionKey, getShowMorePaginationURLByKey);
  return <LoadMoreButton {...{ onClick, shouldDisplay, text }} additionalStyle={moreButtonStyle} />;
};
